import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader, Shield, Database, Trash2, Clock, AlertTriangle, Filter, BookOpen, Stethoscope, FileText, ChevronDown, ChevronUp, Tag, Search, X } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';

function AIResearchPage({ user }) {
  const [query, setQuery] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [dataScope, setDataScope] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [conditionFilter, setConditionFilter] = useState('');
  const [researchMode, setResearchMode] = useState('clinical');
  const [directorFilter, setDirectorFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [studyTypeFilter, setStudyTypeFilter] = useState('all');
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  async function loadHistory() {
    try {
      const h = await window.zignagenixAPI.getAIResearchHistory(20);
      if (!h?.error) setHistory(Array.isArray(h) ? h : []);
    } catch (err) {
      console.error('Failed to load AI research history:', err);
    }
  }

  function buildEnrichedQuery() {
    let enrichedQuery = query;
    const filters = [];

    if (conditionFilter.trim()) {
      filters.push(`Patient Condition: ${conditionFilter}`);
    }

    if (researchMode === 'evidence-based') {
      filters.push('Mode: Provide evidence-based answers grounded in established medical literature, clinical guidelines (ACC/AHA, ADA, GOLD, etc.), and peer-reviewed research. Include relevant clinical guidelines and literature references.');
    } else if (researchMode === 'comparative') {
      filters.push('Mode: Compare patient cohort data against published standards, benchmarks, and clinical trial outcomes. Highlight variations from established norms.');
    } else if (researchMode === 'outcomes') {
      filters.push('Mode: Focus on treatment outcomes, readmission rates, clinical outcomes, quality metrics, and effectiveness measures.');
    }

    if (directorFilter.trim()) {
      filters.push(`Research Director: ${directorFilter}`);
    }
    if (departmentFilter !== 'all') {
      filters.push(`Research Department: ${departmentFilter}`);
    }
    if (studyTypeFilter !== 'all') {
      filters.push(`Study Type: ${studyTypeFilter}`);
    }

    if (filters.length > 0) {
      enrichedQuery = `[Research Context: ${filters.join(' | ')}]\n\n${query}`;
    }

    return enrichedQuery;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!query.trim() || loading) return;

    const enrichedQuery = buildEnrichedQuery();
    const userMsg = { role: 'user', text: query, timestamp: new Date().toISOString() };
    setConversation(prev => [...prev, userMsg]);
    setQuery('');
    setLoading(true);

    try {
      const result = await window.zignagenixAPI.queryAIResearch(enrichedQuery, dataScope);
      const aiMsg = {
        role: 'assistant',
        text: result.error ? `Error: ${result.message}` : result.response,
        recordsAnalyzed: result.recordsAnalyzed || 0,
        dataScope: result.dataScope || dataScope,
        timestamp: new Date().toISOString(),
        isError: !!result.error,
      };
      setConversation(prev => [...prev, aiMsg]);
      loadHistory();
    } catch (err) {
      setConversation(prev => [...prev, {
        role: 'assistant', text: 'Failed to process query. Please try again.', isError: true,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  }

  function clearConversation() {
    setConversation([]);
  }

  const scopeOptions = [
    { value: 'all', label: 'All Data' },
    { value: 'patients', label: 'Patient Demographics' },
    { value: 'encounters', label: 'Encounters' },
    { value: 'labs', label: 'Lab Results' },
    { value: 'medications', label: 'Medications' },
    { value: 'vitals', label: 'Vitals' },
    { value: 'conditions', label: 'Patient Conditions' },
  ];

  const departmentOptions = [
    'All Departments',
    'Cardiology',
    'Endocrinology',
    'Pulmonology',
    'Oncology',
    'Neurology',
    'Psychiatry',
    'Infectious Disease',
    'Orthopedics',
    'Pediatrics',
    'Obstetrics/Gynecology',
    'General Medicine',
  ];

  const studyTypeOptions = [
    'All Types',
    'Retrospective',
    'Prospective',
    'Case Study',
    'Cohort Analysis',
    'Quality Improvement',
    'Outcomes Research',
  ];

  const researchModes = [
    { value: 'clinical', label: 'Clinical Query', icon: Stethoscope, description: 'Query patient data directly' },
    { value: 'evidence-based', label: 'Evidence-Based', icon: BookOpen, description: 'Ground in clinical guidelines & literature' },
    { value: 'comparative', label: 'Comparative Analysis', icon: FileText, description: 'Compare against published standards' },
    { value: 'outcomes', label: 'Outcomes Research', icon: Tag, description: 'Focus on treatment outcomes & metrics' },
  ];

  const activeFilters = [
    conditionFilter && { type: 'Condition', value: conditionFilter },
    researchMode !== 'clinical' && { type: 'Mode', value: researchModes.find(m => m.value === researchMode)?.label || '' },
    directorFilter && { type: 'Director', value: directorFilter },
    departmentFilter !== 'all' && { type: 'Department', value: departmentFilter },
    studyTypeFilter !== 'all' && { type: 'Study Type', value: studyTypeFilter },
  ].filter(Boolean);

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: 0 }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #2E8B98, #256F7A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bot size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>AI Research Assistant</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Query de-identified patient data with Claude AI</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PrintButton pageTitle="AI Research Assistant" subtitle="De-identified Patient Data Queries" />
          <div style={{ background: '#f0fdf4', padding: '6px 12px', borderRadius: 6, fontSize: 12, color: '#15803d', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Shield size={12} /> HIPAA Safe Harbor — All PHI auto-redacted
          </div>
          <button onClick={() => setShowHistory(!showHistory)} style={{
            background: showHistory ? '#e0f4f8' : '#F8F9FA', border: 'none',
            padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
            color: showHistory ? '#2E8B98' : '#6C757D', boxShadow: NEU.shadow.xs,
          }}>
            <Clock size={12} /> History
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div style={{ padding: '12px 24px', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#e0f4f8', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#2E8B98', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Filter size={14} /> Active Filters:
          </span>
          {activeFilters.map((filter, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#2E8B98', color: '#fff', fontSize: 12,
              padding: '4px 10px', borderRadius: 12,
            }}>
              <span>{filter.type}: {filter.value}</span>
              <button
                onClick={() => {
                  if (filter.type === 'Condition') setConditionFilter('');
                  if (filter.type === 'Mode') setResearchMode('clinical');
                  if (filter.type === 'Director') setDirectorFilter('');
                  if (filter.type === 'Department') setDepartmentFilter('all');
                  if (filter.type === 'Study Type') setStudyTypeFilter('all');
                }}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Main Chat */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Research Filters Panel */}
          {showFilters && (
            <div style={{ padding: '16px 24px', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA', maxHeight: '50vh', overflowY: 'auto' }}>
              {/* Condition Explorer */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Search size={14} /> Condition Explorer
                </label>
                <input
                  type="text"
                  value={conditionFilter}
                  onChange={(e) => setConditionFilter(e.target.value)}
                  placeholder="e.g., Type 2 Diabetes, Hypertension, COPD, ICD-10 code..."
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: 'none',
                    fontSize: 13, outline: 'none', transition: 'boxShadow 0.15s', boxSizing: 'border-box', background: '#F8F9FA', boxShadow: NEU.shadow.insetSm,
                  }}
                  onFocus={e => e.target.style.boxShadow = NEU.shadow.inset}
                  onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
                />
              </div>

              {/* Research Mode Selector */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BookOpen size={14} /> Research Mode
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {researchModes.map((mode) => {
                    const IconComponent = mode.icon;
                    return (
                      <button
                        key={mode.value}
                        onClick={() => setResearchMode(mode.value)}
                        style={{
                          padding: '12px 10px', borderRadius: 12, border: 'none',
                          background: researchMode === mode.value ? '#e0f4f8' : '#F8F9FA',
                          cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', boxShadow: NEU.shadow.xs,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <IconComponent size={16} color={researchMode === mode.value ? '#2E8B98' : '#6C757D'} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: researchMode === mode.value ? '#2E8B98' : '#212529' }}>{mode.label}</span>
                        </div>
                        <span style={{ fontSize: 11, color: '#6C757D', display: 'block' }}>{mode.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Evidence-Based Banner */}
              {researchMode === 'evidence-based' && (
                <div style={{
                  padding: '10px 12px', borderRadius: 8, background: '#e0f4f8',
                  border: 'none', boxShadow: NEU.shadow.xs, marginBottom: 20, fontSize: 12,
                  color: '#2E8B98', display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <BookOpen size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>Responses include evidence-based references from established medical guidelines and peer-reviewed literature.</span>
                </div>
              )}

              {/* Director & Department Filters */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Stethoscope size={14} /> Director of Research
                  </label>
                  <input
                    type="text"
                    value={directorFilter}
                    onChange={(e) => setDirectorFilter(e.target.value)}
                    placeholder="Name..."
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none',
                      fontSize: 13, outline: 'none', transition: 'boxShadow 0.15s', boxSizing: 'border-box', background: '#F8F9FA', boxShadow: NEU.shadow.insetSm,
                    }}
                    onFocus={e => e.target.style.boxShadow = NEU.shadow.inset}
                    onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FileText size={14} /> Research Department
                  </label>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none',
                      fontSize: 13, background: '#F8F9FA', color: '#212529', cursor: 'pointer', boxSizing: 'border-box', boxShadow: NEU.shadow.insetSm,
                    }}
                  >
                    {departmentOptions.map((dept) => (
                      <option key={dept} value={dept === 'All Departments' ? 'all' : dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Study Type Filter */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag size={14} /> Study Type
                </label>
                <select
                  value={studyTypeFilter}
                  onChange={(e) => setStudyTypeFilter(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none',
                    fontSize: 13, background: '#F8F9FA', color: '#212529', cursor: 'pointer', boxSizing: 'border-box', boxShadow: NEU.shadow.insetSm,
                  }}
                >
                  {studyTypeOptions.map((type) => (
                    <option key={type} value={type === 'All Types' ? 'all' : type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
            {conversation.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#ADB5BD' }}>
                <Bot size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#6C757D', marginBottom: 8 }}>Ask anything about your patient data</h3>
                <p style={{ fontSize: 13, maxWidth: 500, margin: '0 auto 24px' }}>
                  All queries run against de-identified data. Patient names, MRNs, dates of birth, and other PHI identifiers are automatically redacted from results.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 600, margin: '0 auto' }}>
                  {[
                    'What is the average A1c across all diabetic patients?',
                    'How many patients have life-threatening allergies?',
                    'Show encounter volume breakdown by type',
                    'What are the most commonly prescribed medications?',
                    'Summarize abnormal lab results from this month',
                  ].map((s) => (
                    <button key={s} onClick={() => setQuery(s)} style={{
                      background: '#F8F9FA', border: 'none', borderRadius: 12,
                      padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: '#212529',
                      textAlign: 'left', transition: 'all 0.15s', boxShadow: NEU.shadow.xs,
                    }}
                    onMouseEnter={e => { e.target.style.background = '#e0f4f8'; e.target.style.boxShadow = NEU.shadow.sm; }}
                    onMouseLeave={e => { e.target.style.background = '#F8F9FA'; e.target.style.boxShadow = NEU.shadow.xs; }}
                    >{s}</button>
                  ))}
                </div>
              </div>
            )}

            {conversation.map((msg, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 16,
              }}>
                <div style={{
                  maxWidth: '75%', padding: '12px 16px', borderRadius: 12,
                  background: msg.role === 'user' ? '#2E8B98' : msg.isError ? '#fef2f2' : '#F8F9FA',
                  color: msg.role === 'user' ? '#fff' : msg.isError ? '#DC3545' : '#212529',
                  border: 'none', boxShadow: msg.role === 'user' ? NEU.shadow.sm : msg.isError ? NEU.shadow.xs : NEU.shadow.xs,
                }}>
                  {msg.role === 'assistant' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 11, color: '#ADB5BD' }}>
                      <Bot size={12} /> Claude AI
                      {msg.recordsAnalyzed > 0 && (
                        <span style={{ marginLeft: 8 }}><Database size={10} /> {msg.recordsAnalyzed} records analyzed</span>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                  <div style={{ fontSize: 10, color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#ADB5BD', marginTop: 6, textAlign: 'right' }}>
                    {msg.timestamp?.substring(11, 16)}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 16px', color: '#2E8B98', fontSize: 13 }}>
                <Loader size={16} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                Analyzing de-identified data...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '12px 24px 16px', borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)', background: '#F8F9FA' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  background: showFilters ? '#e0f4f8' : '#F8F9FA',
                  border: 'none',
                  padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  color: showFilters ? '#2E8B98' : '#6C757D',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'all 0.15s', boxShadow: NEU.shadow.xs,
                }}
              >
                <Filter size={14} /> Research Filters
                {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <select value={dataScope} onChange={(e) => setDataScope(e.target.value)} style={{
                padding: '6px 10px', borderRadius: 8, border: 'none', fontSize: 12,
                background: '#F8F9FA', color: '#212529', cursor: 'pointer', boxShadow: NEU.shadow.xs,
              }}>
                {scopeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {conversation.length > 0 && (
                <button onClick={clearConversation} style={{
                  background: '#F8F9FA', border: 'none', borderRadius: 8,
                  padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: '#6C757D',
                  display: 'flex', alignItems: 'center', gap: 4, boxShadow: NEU.shadow.xs,
                }}>
                  <Trash2 size={12} /> Clear
                </button>
              )}
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a research question about de-identified patient data..."
                disabled={loading}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 12, border: 'none',
                  fontSize: 14, outline: 'none', transition: 'boxShadow 0.15s', background: '#F8F9FA', boxShadow: NEU.shadow.insetSm,
                }}
                onFocus={e => e.target.style.boxShadow = NEU.shadow.inset}
                onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
              />
              <button type="submit" disabled={loading || !query.trim()} style={{
                padding: '10px 16px', borderRadius: 12, border: 'none',
                background: loading || !query.trim() ? '#d1d5db' : '#2E8B98',
                color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 14,
                boxShadow: `${NEU.shadow.sm}, inset 0 1px 0 rgba(255,255,255,0.3)`,
              }}>
                <Send size={16} /> Ask
              </button>
            </form>
          </div>
        </div>

        {/* History Panel */}
        {showHistory && (
          <div style={{ width: 280, borderLeft: 'none', boxShadow: 'inset 1px 0 0 rgba(186,199,216,0.45)', overflowY: 'auto', background: '#F8F9FA', padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#212529' }}>Query History</h3>
            {history.length === 0 && <p style={{ fontSize: 13, color: '#ADB5BD' }}>No queries yet</p>}
            {history.map((h) => (
              <div key={h.id} style={{
                padding: '10px 12px', marginBottom: 8, background: '#F8F9FA', borderRadius: 12,
                border: 'none', boxShadow: NEU.shadow.xs, cursor: 'pointer', fontSize: 12,
              }}
              onClick={() => setQuery(h.query_text)}
              >
                <div style={{ fontWeight: 600, color: '#212529', marginBottom: 4, lineHeight: 1.3 }}>
                  {h.query_text.length > 80 ? h.query_text.substring(0, 80) + '...' : h.query_text}
                </div>
                <div style={{ color: '#ADB5BD', display: 'flex', gap: 8 }}>
                  <span>{h.data_scope || 'all'}</span>
                  <span>{h.records_analyzed || 0} records</span>
                  <span>{h.created_at?.substring(0, 10)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

export default AIResearchPage;
